import IcEdit from '@assets/details-camera-icons/IcEdit';
import ICDelete from '@assets/jsx-icon/ICDelete';
import ICNote from '@assets/jsx-icon/ICNote';
import BaseListNote, { tableHeaders } from '@components/BaseListNote';
import PopUpWarning from '@components/PopUpWarning';
import { CellOfRow } from '@components/tables';
import { pushError, pushSuccess } from '@components/toast';
import { verifierNoteHttpController } from '@controllers/index';
import { ListProps } from '@Core';
import { VerificationNote } from '@EcamModel/model/VerificationNote';
import usePopUp from '@hooks/usePopUp';
import { Box, IconButton, Stack, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { Filter } from '@pages/cameras/list';
import { DangerTooltip } from '@pages/cameras/list/components/TooltipDangerIconButton';
import { useContext, useEffect, useState } from 'react';
import { MdAdd } from 'react-icons/md';
import { formatDateTime, getFirstAndLastDayOfCurrentWeek } from 'src/helpers';
import { PotentialContraventionContext } from '../hooks/PotentialContraventionProvider';
import DetailInfoSection from './DetailInfoSection';
import { Mode } from './PCDetail';
import PopUpAddNoteVerifier from './PopUpAddNoteVerifier';
import { useBackdrop } from 'src/providers/BackdropProvider';
import ListNoData from '@pages/cameras/list/components/NoDataCameras';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { AxiosError } from '@controllers/axiosClient';

type Props = {
    onCountPCNNote(counts: number): void;
};

export default function NoteVerifier(props: Props) {
    const { onCountPCNNote } = props;
    const queryClient = useQueryClient();

    const [mode, setMode] = useState<Mode>(Mode.create);
    const popUpAddOrUpdateNoteVerifier = usePopUp();
    const popUpConfirmDeleteNoteHistory = usePopUp();

    const pcContext = useContext(PotentialContraventionContext);
    const backdrop = useBackdrop();

    const { contraventionId, groupPc } = pcContext;

    const { fromCurrentWeek, toCurrentWeek } = getFirstAndLastDayOfCurrentWeek();

    const [filter, setFilter] = useState<Partial<Filter>>({
        page: 1,
        from: fromCurrentWeek,
        to: toCurrentWeek,
        search: '',
    });

    const [idNote, setIdNote] = useState<number>(0);

    const handleGetListNote = async () => {
        const _filter: ListProps<VerificationNote> = {
            page: filter.page ?? 1,
            pageSize: 10,
            sorts: ['-Updated'],
            filter: {
                GroupPotentialContraventionId: groupPc?.PotentialContraventions![0].GroupPotentialContraventionId ?? 0,
            },
        };

        const response = await verifierNoteHttpController.list(_filter);
        onCountPCNNote(response?.rows?.length);
        return response;
    };

    const {
        data: listNotes,
        error: errorListNotes,
        isLoading: isLoadingListNotes,
    } = useQuery(['listNotes', contraventionId, filter.page], handleGetListNote, {
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const upsertCameraNoteAPI = async (data: VerificationNote) => {
        const response = await verifierNoteHttpController.upsert(data);
        return response;
    };

    const deleteNoteAPI = async (idNote: number) => {
        const response = await verifierNoteHttpController.delete(idNote.toString());
        return response;
    };

    const mutationUpsertCameraNote = useMutation(upsertCameraNoteAPI, {
        onSuccess: async (data, variables) => {
            queryClient.invalidateQueries(['listNotes']);
            queryClient.invalidateQueries(['trailAuditEventLogPCN']);
            pushSuccess(variables.Id ? 'Update successfully' : 'Add successfully');
        },
        onError: (err: AxiosError) => {
            pushError(err.message);
        },
        onSettled: () => {
            backdrop.setFalse();
        },
    });

    const mutationDeleteNote = useMutation(deleteNoteAPI, {
        onSuccess: async () => {
            queryClient.invalidateQueries(['listNotes']);
            queryClient.invalidateQueries(['trailAuditEventLogPCN']);
            pushSuccess('Delete successfully');
        },
        onError: (err: AxiosError) => {
            pushError(err.message);
        },
        onSettled: () => {
            backdrop.setFalse();
        },
    });

    const handleAddOrUpdateNote = async (note: string, idNote?: number) => {
        backdrop.setTrue();
        const data: VerificationNote = {
            Id: mode === Mode.edit ? idNote : undefined,
            Notes: note,
            GroupPotentialContraventionId: groupPc?.PotentialContraventions![0].GroupPotentialContraventionId ?? 0,
        };

        await mutationUpsertCameraNote.mutateAsync(data);
    };

    const handleDeleteNote = async (idNote: number) => {
        backdrop.setTrue();
        await mutationDeleteNote.mutateAsync(idNote);
    };

    return (
        <Stack>
            <DetailInfoSection
                icon={<ICNote />}
                title="PCN note"
                borderRadius={'0px 8px 8px 8px'}
                action={
                    <>
                        <Tooltip title="Add note" placement="top" arrow>
                            <IconButton
                                sx={{
                                    borderRadius: '3px',
                                    width: '24px',
                                    height: '24px',
                                    color: 'white',
                                    backgroundColor: '#2E7D32',
                                    ':hover': {
                                        backgroundColor: '#256428',
                                    },
                                    padding: '4px',
                                    '&.MuiIconButton-root.Mui-disabled': {
                                        backgroundColor: '#eee',
                                    },
                                }}
                                onClick={() => {
                                    setMode(Mode.create);
                                    popUpAddOrUpdateNoteVerifier.setTrue();
                                }}
                            >
                                <MdAdd style={{ width: '16px', height: '16px' }} />
                            </IconButton>
                        </Tooltip>
                    </>
                }
                content={
                    <BaseListNote
                        paging={listNotes}
                        isLoading={isLoadingListNotes}
                        tableHeaders={[...tableHeaders, '']}
                        styleContainer={{ m: 2 }}
                        isVerificationPage
                        setPage={(page) => {
                            setFilter((prev) => ({ ...prev, page }));
                        }}
                        noDataComponent={
                            <ListNoData
                                sx={{
                                    border: 'none',
                                    minHeight: 100,
                                }}
                            />
                        }
                        renderedItem={(item) => {
                            return (
                                <>
                                    <CellOfRow value={formatDateTime(item?.Created!)} align="left" />
                                    <CellOfRow value={item?.CreatedBy ?? ''} align="left" />
                                    <CellOfRow value={formatDateTime(item?.Updated!)} align="left" />
                                    <TableCell
                                        align="left"
                                        sx={{
                                            wordWrap: 'break-word',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        <Typography>{item?.Notes.trim() ?? ''}</Typography>
                                    </TableCell>
                                    <CellOfRow
                                        value={
                                            <>
                                                <Stack direction={'row'} spacing={0.7} alignItems={'center'}>
                                                    <Tooltip placement="top" title="Edit">
                                                        <Box
                                                            sx={{
                                                                color: '#85858A',
                                                                transition: '.2s ease-in-out',
                                                                cursor: 'pointer',
                                                                '&:hover': {
                                                                    color: (theme) => theme.palette.secondary.main,
                                                                },
                                                            }}
                                                            onClick={() => {
                                                                setMode(Mode.edit);
                                                                setIdNote(item?.Id ?? 0);
                                                                popUpAddOrUpdateNoteVerifier.setTrue();
                                                            }}
                                                        >
                                                            <IcEdit />
                                                        </Box>
                                                    </Tooltip>
                                                    <DangerTooltip placement="top" title="Delete">
                                                        <Box
                                                            sx={{
                                                                cursor: 'pointer',
                                                            }}
                                                            onClick={() => {
                                                                setIdNote(item?.Id ?? 0);
                                                                popUpConfirmDeleteNoteHistory.setTrue();
                                                            }}
                                                        >
                                                            <ICDelete fontSize={18} />
                                                        </Box>
                                                    </DangerTooltip>
                                                </Stack>
                                            </>
                                        }
                                        align="left"
                                    />
                                </>
                            );
                        }}
                    />
                }
            />
            <PopUpAddNoteVerifier
                {...popUpAddOrUpdateNoteVerifier}
                mode={mode}
                listNotes={listNotes!}
                groupPc={groupPc}
                idNote={idNote}
                handleAddOrUpdateNote={handleAddOrUpdateNote}
            />
            <PopUpWarning
                {...popUpConfirmDeleteNoteHistory}
                title="Confirm"
                message="Are you sure you want to delete this note?"
                onClose={() => {
                    setIdNote(0);
                    popUpConfirmDeleteNoteHistory.onClose();
                }}
                onConfirm={() => {
                    if (idNote) {
                        handleDeleteNote(idNote);
                        popUpConfirmDeleteNoteHistory.onClose();
                    } else {
                        pushError('Id note does not exist');
                    }
                }}
            />
        </Stack>
    );
}
